import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PhotoGallery {
  id: string;
  section_key: string;
  images: string[];
  title_fr?: string;
  title_pt?: string;
  title_en?: string;
  is_active: boolean;
  max_images?: number;
}

export const usePhotoGalleries = () => {
  const [galleries, setGalleries] = useState<Record<string, PhotoGallery>>({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchGalleries = async () => {
    try {
      const { data, error } = await supabase
        .from('photo_galleries')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      const galleriesMap: Record<string, PhotoGallery> = {};
      data?.forEach((gallery) => {
        galleriesMap[gallery.section_key] = {
          ...gallery,
          images: Array.isArray(gallery.images) 
            ? (gallery.images as string[]) 
            : []
        };
      });

      setGalleries(galleriesMap);
    } catch (error) {
      console.error('Error fetching galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGalleries();
  }, []);

  const updateGallery = async (sectionKey: string, images: string[]) => {
    try {
      // Chercher si la galerie existe déjà
      const { data: existing } = await supabase
        .from('photo_galleries')
        .select('id')
        .eq('section_key', sectionKey)
        .maybeSingle();

      if (existing) {
        // Mise à jour
        const { error } = await supabase
          .from('photo_galleries')
          .update({ images: images as any })
          .eq('section_key', sectionKey);

        if (error) throw error;
      } else {
        // Création
        const { error } = await supabase
          .from('photo_galleries')
          .insert({
            section_key: sectionKey,
            images: images as any,
            is_active: true
          });

        if (error) throw error;
      }

      // Mise à jour locale
      setGalleries(prev => ({
        ...prev,
        [sectionKey]: {
          id: existing?.id || '',
          section_key: sectionKey,
          images,
          is_active: true
        }
      }));

      toast({
        title: "Galerie mise à jour",
        description: "Les images ont été enregistrées avec succès"
      });

      // Recharger pour être sûr d'avoir les dernières données
      await fetchGalleries();
    } catch (error) {
      console.error('Error updating gallery:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les modifications",
        variant: "destructive"
      });
    }
  };

  return {
    galleries,
    loading,
    updateGallery,
    refetch: fetchGalleries
  };
};
